import * as THREE from 'three';
import { riversOf } from '@/world/rivers';
import { skyColourAlong } from '@/render/sky';
import type { Region } from '@/world/region';

/**
 * The rivers, as water rather than as a tint.
 *
 * The region already knows where every drop goes; this reads its flow field
 * once and lays a ribbon from each strong-flow cell to its steepest downhill
 * neighbour — chained cell to cell, the ribbons become continuous rivers.
 * Width grows with the log of accumulated flow — headwaters are threads, the
 * trunk valleys carry visible water — and wobbles a little cell to cell,
 * because no real river holds one width for a kilometre.
 *
 * Two things give the water presence that the first cut of this lacked:
 *
 * - **It lies on the ground that is actually drawn.** The ribbon used to sit
 *   0.6 m over the *coarse* height field, and the drawn surface adds metres
 *   of relief on top of that — so on a rough valley floor the ground poked
 *   through the water and the water bridged over swales. Every segment is
 *   now subdivided and each vertex seated on `surfaceHeight` — the same
 *   surface everything else in the game stands on.
 *
 * - **It moves.** A time-scrolled ripple tilts the fragment normal, so the
 *   sun's glints slide over the surface. The ripple is world-anchored noise
 *   and fades out by 700 m, exactly like the terrain's micro-relief: from
 *   the air a river is a bright line, and the movement belongs to the range
 *   where water visibly runs.
 *
 * Built once from the immutable Region, deterministic; `update` advances the
 * one time uniform and nothing else.
 */

/**
 * THE WATER IS LAID FROM THE SHARED RIVER MODEL (2026-08-19). `world/rivers.ts`
 * owns the network — the chains, their stations every 12 m, and every
 * station's width, depth, water surface, thalweg, pool phase, speed and
 * curvature — and `surfaceHeight` carves the terrain from the same model, so
 * the water and the channel are one river. This file lays a surface on those
 * stations: SEVEN vertices across each, from bank edge to bank edge, at the
 * station's water height (level across, smooth along), each carrying the
 * local depth beneath it, its place across the channel, its metres
 * downstream, the reach's speed and its pool/riffle phase — the shader's
 * whole vocabulary. The visible-flow threshold lives in the model.
 *
 * Dev: `?water=off` hides the water, so the carved channel can be judged dry
 * (the river's equivalent of the willow's wood-only view).
 */
const ACROSS = [-1, -0.66, -0.33, 0, 0.33, 0.66, 1] as const;

export interface Rivers {
  mesh: THREE.Mesh;
  /** Advance the ripple. Seconds; the caller's one clock. */
  update(tSec: number): void;
  /**
   * Is this point under drawn water? Read from the shared river model — the
   * grass carpet and its flora ask it per cell, so nothing green stands in
   * a river. Render-only knowledge about a render-only thing: the world
   * never asks.
   */
  inWater(x: number, z: number): boolean;
  dispose(): void;
}

const WATER_DEV: string | null = (() => {
  try {
    if (typeof window === 'undefined' || !(import.meta as unknown as { env?: { DEV?: boolean } }).env?.DEV) return null;
    return new URLSearchParams(window.location.search).get('water');
  } catch { return null; }
})();

export function buildRivers(region: Region): Rivers {
  const net = riversOf(region);

  const positions: number[] = [];
  const indices: number[] = [];
  const across: number[] = [];
  const depths: number[] = [];
  const along: number[] = [];
  const speeds: number[] = [];
  const pools: number[] = [];
  const hws: number[] = [];

  for (const chain of net.chains) {
    const st = chain.stations;
    let prevBase = -1;
    for (let k = 0; k < st.length; k++) {
      const s = st[k];
      const nx = -s.tz, nz = s.tx;                 // left of the flow
      const base = positions.length / 3;
      for (let a = 0; a < ACROSS.length; a++) {
        const u = ACROSS[a];
        const d = u * s.halfWidth;
        const x = s.x + nx * d, z = s.z + nz * d;
        positions.push(x, s.waterY, z);
        // the depth beneath this vertex, from the model's own bed
        const r = net.at(x, z);
        depths.push(r ? r.localDepth : 0);
        across.push(u);
        along.push(s.along);
        speeds.push(s.speed);
        pools.push(s.pool);
        hws.push(s.halfWidth);
      }
      if (prevBase >= 0) {
        for (let a = 0; a + 1 < ACROSS.length; a++) {
          const p0 = prevBase + a, p1 = prevBase + a + 1, q0 = base + a, q1 = base + a + 1;
          // wound to face UP (the old strip's lesson: standard materials cull back faces)
          indices.push(p0, p1, q0, p1, q1, q0);
        }
      }
      prevBase = base;
    }
  }

  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(positions), 3));
  geo.setAttribute('aAcross', new THREE.BufferAttribute(new Float32Array(across), 1));
  geo.setAttribute('aDepth', new THREE.BufferAttribute(new Float32Array(depths), 1));
  geo.setAttribute('aAlong', new THREE.BufferAttribute(new Float32Array(along), 1));
  geo.setAttribute('aSpeed', new THREE.BufferAttribute(new Float32Array(speeds), 1));
  geo.setAttribute('aPool', new THREE.BufferAttribute(new Float32Array(pools), 1));
  geo.setAttribute('aHalf', new THREE.BufferAttribute(new Float32Array(hws), 1));
  geo.setIndex(indices);
  geo.computeVertexNormals();
  geo.computeBoundingSphere();

  const time = { value: 0 };
  /*
   * Pale, and NOT metallic. The old 0x3a7292 at metalness 0.45 was almost
   * exactly the ground's own damp-margin tint — the rivers were seated, drawn,
   * and *camouflaged*. Water seen from anywhere above is mostly sky, and sky
   * is bright: this reads beside the sea's shallows as one water.
   *
   * `metalness` was 0.15 and this project has NO environment map anywhere
   * (`grep -rn "envMap\|scene.environment\|PMREMGenerator" src/` returns
   * nothing), so it was the last surviving instance of the tax the sea carried:
   * a specular with nothing to sample, paid for out of the surface's own
   * colour. Three computes the diffuse albedo as `albedo × (1 - metalness)`,
   * so the base colour had been multiplied by 0.85 for its whole life and was
   * chosen by an eye seeing it that way. The colour is therefore scaled by
   * 0.85 in LINEAR space as metalness goes to 0 — the tone is held exactly and
   * only the bogus specular leaves. `0x74aac2` unscaled would have brightened
   * every river in the world by 18% as a side effect of a correctness fix.
   */
  const mat = new THREE.MeshStandardMaterial({
    color: new THREE.Color(0x74aac2).multiplyScalar(0.85),
    roughness: 0.14, metalness: 0,
    // a floor of sky in the water: real water is never darker than what it
    // reflects, and without this a shaded reach went to ground-grey and
    // vanished against its own damp margin.
    //
    // That comment was right and the constant was not. `emissive` is added
    // unconditionally — `outgoingLight = diffuse + specular + emissive` — so a
    // fixed 0x2a4152 is a floor of sky that stays when the sky is gone.
    // Measured over the island's densest reach at 00:28 under 0.84 overcast:
    // **river 51.9 luminance against ground 15.0, 3.5x brighter.** The rivers
    // glowed in the dark, and ACES at 1.55 compresses highlights rather than
    // blacks, so the frame showed it worse than the numbers do. `update()`
    // now writes this every frame from the sky's own colour; the value here is
    // only what the first frame uses before `paintSky` has ever run.
    emissive: 0x2a4152, emissiveIntensity: 0.55,
    transparent: true, opacity: 0.9, depthWrite: false,
    // and both faces, so a rung tilted down a cascade never culls away
    side: THREE.DoubleSide,
  });
  /*
   * THE WATER RUNS (2026-08-19). Three things, all in the river's OWN
   * coordinates — metres downstream (`aAlong`) and across the bed
   * (`aAcross`, −1 bank to +1 bank) — rather than world xz, which is what
   * lets a reach that bends still flow along itself:
   *
   *  · the ripple octaves scroll DOWNSTREAM (they used to drift in one fixed
   *    world direction, so a river bending back on itself flowed uphill);
   *  · the banks feather — alpha falls over the outer 30% of the width and
   *    the water is darkest and most opaque down the middle, so the ribbon
   *    meets the bank as shallows rather than as a cut edge;
   *  · white water where the bed is steep (`aSteep`): streaks of foam drawn
   *    downstream, and more of them along the banks where water breaks.
   *
   * And a Fresnel term on the sky floor: looked down into, water shows its
   * own colour; looked along, it is the sky. The emissive floor — which IS
   * the sky, a quarter strength — is scaled by a grazing-angle term so a far
   * reach brightens toward the sky it reflects and a reach underfoot does
   * not glow.
   */
  /*
   * DEPTH BIAS, as a fraction of view distance — falls.ts's trick, for the same
   * reason: the water now lies BELOW the land in a carved channel, and a far
   * chunk's coarse lattice does not resolve a 10–30 m channel, so at range the
   * uncarved terrain would bury the water. Pulling every vertex toward the eye
   * by this fraction of its distance moves only its depth (the perspective
   * divide cancels a uniform view-space scale), so a far river wins the depth
   * test against a bank up to ~4 m above it at 1 km and reads as the line it
   * is; close in the bias is centimetres and the channel does the work.
   */
  const DEPTH_BIAS = 0.004;
  mat.onBeforeCompile = (sh) => {
    sh.uniforms.uRiver = time;
    sh.vertexShader = sh.vertexShader
      .replace('#include <common>', `#include <common>
      varying vec3 vRivW;
      attribute float aAcross; attribute float aDepth; attribute float aAlong;
      attribute float aSpeed; attribute float aPool; attribute float aHalf;
      varying float vAcross; varying float vDepth; varying float vAlong;
      varying float vSpeed; varying float vPool; varying float vHalf;`)
      .replace('#include <begin_vertex>',
        `#include <begin_vertex>
        vRivW = (modelMatrix * vec4(transformed, 1.0)).xyz;
        vAcross = aAcross; vDepth = aDepth; vAlong = aAlong;
        vSpeed = aSpeed; vPool = aPool; vHalf = aHalf;`)
      .replace('#include <project_vertex>',
        `#include <project_vertex>
  mvPosition.xyz *= ${(1 - DEPTH_BIAS).toFixed(4)};
  gl_Position = projectionMatrix * mvPosition;`);
    sh.fragmentShader = sh.fragmentShader
      .replace('#include <common>', `#include <common>
      varying vec3 vRivW;
      varying float vAcross; varying float vDepth; varying float vAlong;
      varying float vSpeed; varying float vPool; varying float vHalf;
      uniform float uRiver;
      float rRipple = 0.0;   // the surface's local tilt, set by the ripple block, read by the colour block
      float rHash(vec2 i){
        vec3 q = fract(vec3(i.x, i.y, i.x) * 0.1031);
        q += dot(q, q.yzx + 33.33);
        return fract((q.x + q.y) * q.z);
      }
      float rNoise(vec2 p){
        vec2 i = floor(p), f = p - i;
        vec2 u = f * f * (3.0 - 2.0 * f);
        float a = rHash(i), b = rHash(i + vec2(1.0, 0.0));
        float c = rHash(i + vec2(0.0, 1.0)), d = rHash(i + vec2(1.0, 1.0));
        return mix(mix(a, b, u.x), mix(c, d, u.x), u.y);
      }`)
      .replace('#include <normal_fragment_maps>', `#include <normal_fragment_maps>
      {
        // running water: ripple octaves scrolled DOWNSTREAM in the river's own
        // (along, across) coordinates tilt the normal, so the sun's glints
        // slide with the current — tighter and quicker on a fast reach,
        // broader and slower on a pool. Sub-pixel past 700 m.
        float fade = 1.0 - smoothstep(250.0, 700.0, length(vViewPosition));
        if (fade > 0.002) {
          float t = uRiver;
          float sp = 0.6 + vSpeed * 2.8;                       // metres a second, downstream
          float wl = mix(3.4, 1.3, vSpeed);                    // ripple wavelength, metres
          vec2 p = vec2(vAlong - t * sp, vAcross * vHalf * 0.8 + vRivW.x * 0.02);
          vec2 g = vec2(
            rNoise(p / wl) - rNoise(p / wl + vec2(0.31, 0.0)),
            rNoise(p / (wl * 0.38) + vec2(0.0, t * 0.2)) - rNoise(p / (wl * 0.38) + vec2(0.0, 0.27 + t * 0.2))
          ) * (0.35 + vSpeed * 0.45) * fade;
          normal = normalize(normal + vec3(g.x, 0.0, g.y));
          rRipple = (g.x + g.y) * 2.0;
        }
      }`)
      .replace('#include <emissivemap_fragment>', `#include <emissivemap_fragment>
      {
        vec3 V = normalize(vViewPosition);
        float grazing = pow(1.0 - clamp(dot(normal, V), 0.0, 1.0), 3.0);
        // DEPTH COLOUR: looked into, shallow water is pale and clear — the bed
        // shows through it — and deep water is its own darker, richer
        // blue-green; the sky floor (emissive) comes in with the grazing angle,
        // so a far reach is the sky and a reach underfoot is the river
        float deep = smoothstep(0.0, 1.6, vDepth);
        // restrained: the shallows a pale grey-green over visible gravel, the
        // deeps a darker blue-green — never neon, never black
        vec3 shallowTint = vec3(0.80, 0.84, 0.78);
        vec3 deepTint = vec3(0.30, 0.42, 0.46);
        diffuseColor.rgb *= mix(mix(shallowTint, deepTint, deep), vec3(0.72, 0.74, 0.74), grazing * 0.6);
        // the sky floor: a pool's surface is quieter and holds more sky, a
        // riffle less; DESATURATED toward its own luminance (the sky at 40°
        // is a saturated blue and a river is not a swimming pool), and
        // textured by the ripple, so a surface the sky fills is still a
        // surface and not a sheet
        float eLum = dot(totalEmissiveRadiance, vec3(0.2126, 0.7152, 0.0722));
        totalEmissiveRadiance = mix(vec3(eLum), totalEmissiveRadiance, 0.55)
          * (0.18 + 0.62 * grazing) * (0.9 + 0.2 * vPool) * (1.0 + rRipple * 0.9);
        float near = 1.0 - smoothstep(120.0, 500.0, length(vViewPosition));
        float t = uRiver;
        // white water: where the reach is fast and shallow — a riffle — pale
        // streaks drawn downstream, thickest along the banks where the water
        // breaks; a pool carries almost none
        float riffle = smoothstep(0.62, 0.98, vSpeed) * (1.0 - smoothstep(0.5, 1.4, vDepth));
        float bank = smoothstep(0.6, 0.98, abs(vAcross));
        float foamN = rNoise(vec2((vAlong - t * (1.5 + vSpeed * 2.5)) / 1.4, vAcross * vHalf * 0.6 + vRivW.z * 0.02));
        // sparing: streaks, never a border — a riffle's middle carries a few,
        // its banks a few more, a pool's banks almost none
        float foam = (riffle * 0.45 + bank * riffle * 0.4 + bank * 0.05) * smoothstep(0.66, 0.90, foamN) * near;
        diffuseColor.rgb = mix(diffuseColor.rgb, vec3(0.82, 0.86, 0.88), foam * 0.85);
        totalEmissiveRadiance += vec3(0.10, 0.11, 0.12) * foam;
      }`)
      // the banks and the shallows: the water's alpha feathers to nothing at
      // the bank and follows the depth — a shelf is clear, a pool is opaque
      .replace('#include <opaque_fragment>', `
        float edge = 1.0 - smoothstep(0.80, 1.0, abs(vAcross));
        float column = 0.45 + 0.55 * smoothstep(0.0, 1.2, vDepth);
        diffuseColor.a *= edge * column;
        #include <opaque_fragment>`);
  };

  /*
   * The sky floor, per channel, and where these three numbers come from.
   *
   * They are `shippedEmissiveLinear / middaySkyLinear`, channel by channel,
   * measured off the LIVE sky sample at the canonical clear midday
   * (t = 3153904128), so a clear midday reproduces the shipped
   * `0x2a4152 x 0.55` and nothing about the daylight look moves. What changes
   * is everything else: the floor now falls with the sky, so a river is dark at
   * midnight, grey under a lid and warm at sunset instead of emitting the same
   * cold blue for ever.
   *
   * **The first version of these was derived rather than measured, and was
   * wrong by up to 3.5x.** It divided by the sky's HORIZON colour, because that
   * is the band the sea reflects — but a river is looked down on, so this
   * samples 40 degrees up, where the sky is deeper and darker. The frame still
   * looked right; the numbers said the red channel had dropped to 29% of the
   * shipped floor. Read the sample you actually take, not the one next to it.
   *
   * The corrected factors are near-FLAT (0.234 / 0.273 / 0.219), and that
   * retires a nice story that turned out to be an artifact of the wrong
   * divisor: the shipped constant is not strongly red-damped "water blue", it
   * is simply **the sky at about a quarter strength**, with a slight green lift
   * as the only real tint.
   */
  const SKY_FLOOR_R = 0.2336, SKY_FLOOR_G = 0.2730, SKY_FLOOR_B = 0.2187;
  /** Rivers are looked down on, so the reflected ray leaves at a steeper angle than the sea's. */
  const SKY_SAMPLE_EL = 40 * Math.PI / 180;
  const skyDir = new THREE.Vector3(0, Math.sin(SKY_SAMPLE_EL), -Math.cos(SKY_SAMPLE_EL));
  const skyCol = new THREE.Color();
  /** Display -> linear, matching Three's own transfer, since `skyColourAlong` returns display. */
  const toLinear = (c: number) => (c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);

  const mesh = new THREE.Mesh(geo, mat);
  mesh.renderOrder = 1;
  mesh.receiveShadow = true;
  if (WATER_DEV === 'off') mesh.visible = false;
  return {
    mesh,
    update(tSec: number) {
      time.value = tSec;
      // main.ts calls this at :1743 and `paintSky` at :1746, so the sample is
      // one frame stale — far below the quantisation of the colour itself, and
      // recorded so nobody reads it as a bug.
      skyColourAlong(skyDir, skyCol);
      mat.emissive.setRGB(
        toLinear(skyCol.r) * SKY_FLOOR_R,
        toLinear(skyCol.g) * SKY_FLOOR_G,
        toLinear(skyCol.b) * SKY_FLOOR_B,
      );
    },
    inWater: (x, z) => { const r = net.at(x, z); return !!r && r.inWater; },
    dispose() {
      geo.dispose();
      mat.dispose();
    },
  };
}
